import React, { useEffect, useState } from 'react';
import { ScenarioDetail } from './ScenarioDetail';
import { ScenarioSummary } from './ScenarioSummary';
import { ScenarioHistory } from './ScenarioHistory';
import { Scenario, ScenarioData } from '../../interface/ReasearchData';
import { useParams } from 'react-router-dom';
import StockService from '../../Api_Module/Api_Services/StockService';
import LoaderHelper from '../../utility/Loading/LoaderHelper';
import { useDispatch, useSelector } from 'react-redux';
import { setLastScenario, setScenario, updateLastScenarioCalcBase, updateLastScenarioItem, updateLastScenarioName, updateLastScenarioProjection } from '../../store/ResearchDataSlice';
import { selectResearchData } from '../../store/ResearchDataSlice';
import { alertErrorMessage, alertSuccessMessage } from '../../utility/CustomAlertMessage';
import AuthService from '../../Api_Module/Api_Services/AuthService';
export type ScenarioType = 'bull' | 'base' | 'bear';

export function ScenarioEditor() {
  const [activeScenario, setActiveScenario] = useState<ScenarioType>('bull');
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isCardsSectionCollapsed, setIsCardsSectionCollapsed] = useState(false);
  const scenarios = useSelector((state: any) => state.researchData.lastScenario);
  const marketCurrentPrice = useSelector((state: any) => state.app.marketCurrentPrice);
  const researchData = useSelector(selectResearchData);
  const [recentScenarioSets, setRecentScenarioSets] = useState<Scenario[]>([]);
  const [historyData, setHistoryData] = useState<ScenarioData[]>([]);
  // Calculation base toggle: 'prior' (default) or 'ttm'
  const [calcBase, setCalcBase] = useState<'prior' | 'ttm'>('prior');

  const { symbol } = useParams<{ symbol?: string }>();
  const dispatch = useDispatch();
  const getAnalystForecasts = async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await StockService.getAnalystForecasts(symbol);
      LoaderHelper.loaderStatus(false);
      if (result.forecasts && result.forecasts.length > 0) {
        result.forecasts.sort((a: any, b: any) => a.fiscalYear - b.fiscalYear)
        const analystForecasts: ScenarioData[] = [];
        result.forecasts.map((forecast: any) => {
          const year = forecast.fiscalYear;
          analystForecasts.push({
            isEditableYear: (forecast?.isEditableYear != null)?true:false,
            yearLabel: forecast.periodLabel,
            fiscalYear: year,
            revenue: (forecast.revenue !== null ? forecast.revenue : 0),
            eps: forecast.eps !== null ? forecast.eps : 0,
            revenueGrowth: forecast.revenueGrowth !== null ? forecast.revenueGrowth : '',
            epsGrowth: forecast.epsGrowth !== null ? forecast.epsGrowth : '',
            costOfRevenue: forecast.costOfRevenue !== null ? forecast.costOfRevenue : 0,
            grossProfit: forecast.grossProfit !== null ? forecast.grossProfit : 0,
            netIncome: forecast.netIncome !== null ? forecast.netIncome : 0,
            netIncomeGrowth: forecast.netIncomeGrowth !== null ? forecast.netIncomeGrowth : '',
            netMargins: forecast.netMargins !== null ? forecast.netMargins : '',
            peLow: forecast.peLow !== null ? forecast.peLow : 0,
            peHigh: forecast.peHigh !== null ? forecast.peHigh : 0,
            sharePriceLow: forecast.sharePriceLow !== null ? forecast.sharePriceLow : 0,
            sharePriceHigh: forecast.sharePriceHigh !== null ? forecast.sharePriceHigh : 0,
            sharesOutstanding: forecast.sharesOutstanding !== null ? forecast.sharesOutstanding : 0,
            isThirdYearProjection: forecast.isThirdYearProjection !== null ? forecast.isThirdYearProjection : false,
            isFifthYearProjection: forecast.isFifthYearProjection !== null ? forecast.isFifthYearProjection : false,
          } as ScenarioData);
        });
        setHistoryData(analystForecasts);
        if (!researchData.scenario || researchData.scenario.length === 0) {
          dispatch(setLastScenario({
            name: 'Undefined Scenario',
            calcBase: 'prior',
            bull: {
              data: analystForecasts,
              thirdYearProjectionLabel: '',
              thirdYearProjectionSharePriceLow: '$0',
              thirdYearProjectionSharePriceHigh: '$0',
              thirdYearProjectionCGR: '0%-0%',
              fifthYearProjectionLabel: '',
              fifthYearProjectionSharePriceLow: '$0',
              fifthYearProjectionSharePriceHigh: '$0',
              fifthYearProjectionCGR: '0%-0%',
            },
            base: {
              data: analystForecasts,
              thirdYearProjectionLabel: '',
              thirdYearProjectionSharePriceLow: '$0',
              thirdYearProjectionSharePriceHigh: '$0',
              thirdYearProjectionCGR: '0%-0%',
              fifthYearProjectionLabel: '',
              fifthYearProjectionSharePriceLow: '$0',
              fifthYearProjectionSharePriceHigh: '$0',
              fifthYearProjectionCGR: '0%-0%',
            },
            bear: {
              data: analystForecasts,
              thirdYearProjectionLabel: '',
              thirdYearProjectionSharePriceLow: '$0',
              thirdYearProjectionSharePriceHigh: '$0',
              thirdYearProjectionCGR: '0%-0%',
              fifthYearProjectionLabel: '',
              fifthYearProjectionSharePriceLow: '$0',
              fifthYearProjectionSharePriceHigh: '$0',
              fifthYearProjectionCGR: '0%-0%',
            },
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching analyst forecasts:", error);
    }
  };
  useEffect(() => {
    getAnalystForecasts();
  }, [symbol]);

  const isEmpty = (value:any)=>{
    return value === "" || value === undefined;
  }
  const convertBillions = (value:any) => {
    return `${(value / 1e9).toFixed(2)}B`;
  }

  const updateMetricData = (metric: keyof ScenarioData, year: string, value: any, label: string) => {
    dispatch(updateLastScenarioItem({
      metric,
      year,
      value,
      type: activeScenario,
      label
    }));
  }
  const updatedScenariosData = (label:any, value:any)=>{
    dispatch(updateLastScenarioProjection({
      type: activeScenario,
      label,
      value
    }));
  }
  const updateScenario = (metric: keyof ScenarioData, year: string, value: any, label: string, index: number) => {
    const currentData = scenarios[activeScenario].data || [];
    // Determine base row depending on calcBase toggle
    const ttmIndex = currentData.findIndex((d: any) => (d?.yearLabel ?? '').toString().toUpperCase() === 'TTM');
    const ttmData = ttmIndex !== -1 ? currentData[ttmIndex] : undefined;
    console.log("calcBase", calcBase);
    let priorYearData;
    if(calcBase === 'ttm'){
        priorYearData = currentData[index - 1];
    } else {
        priorYearData = currentData[index - 1]['yearLabel'].toString().toUpperCase() === 'TTM'
        ? currentData[index - 2] : currentData[index - 1];
    }
    const baseData = priorYearData;
    console.log("baseData", baseData);
    if(currentData.length > 0) {
      if(metric === "netIncomeGrowth" && !isEmpty(value)) {
        let NetIncome =  baseData?.netIncome?.value * (1 + (value / 100)) || 0;
        let EPS = ((NetIncome / (baseData?.sharesOutstanding?.value || 0))) || 0;
        let sharesOutstanding = baseData?.sharesOutstanding?.value || 0;
        updateMetricData("netIncome", year, {value:NetIncome, formattedValue:convertBillions(NetIncome)}, label);
        updateMetricData("eps", year, {value:EPS, formattedValue:EPS.toFixed(2)}, label);
        updateMetricData("sharesOutstanding", year, {value:sharesOutstanding, formattedValue:convertBillions(sharesOutstanding)}, label);
        if(EPS && !isEmpty(currentData?.[index]?.peLow?.value)) {
          let sharePriceLow = EPS * currentData?.[index]?.peLow?.value || 0;
          updateMetricData("sharePriceLow", year, {value:sharePriceLow, formattedValue:sharePriceLow.toFixed(2)}, label);
        }
        if(EPS && !isEmpty(currentData?.[index]?.peHigh?.value)) {
          let sharePriceHigh = EPS * currentData?.[index]?.peHigh?.value || 0;
          updateMetricData("sharePriceHigh", year, {value:sharePriceHigh, formattedValue:sharePriceHigh.toFixed(2)}, label);
        }
      }
      if(metric == "revenueGrowth" && !isEmpty(value)) {
        let Revenue =  baseData?.revenue?.value * (1 + (value / 100)) || 0;
        updateMetricData("revenue", year, {value:Revenue, formattedValue:convertBillions(Revenue)}, label);
      }

      let sharePriceLow = currentData?.[index]?.sharePriceLow?.value || 0;
      let sharePriceHigh = currentData?.[index]?.sharePriceHigh?.value || 0;
      if(metric === "peLow" && !isEmpty(value)) {
        sharePriceLow = currentData?.[index]?.eps?.value * value || 0;
        updateMetricData("sharePriceLow", year, {value:sharePriceLow, formattedValue:sharePriceLow.toFixed(2)}, label);
        if(currentData?.[index]?.isThirdYearProjection){
          updatedScenariosData("thirdYearProjectionSharePriceLow", `$${sharePriceLow.toFixed(2)}`);
        }
        if(currentData?.[index]?.isFifthYearProjection){
          updatedScenariosData("fifthYearProjectionSharePriceLow", `$${sharePriceLow.toFixed(2)}`);
        }
      }
      if(metric === "peHigh" && !isEmpty(value)) {
        sharePriceHigh = currentData?.[index]?.eps?.value * value || 0;
        updateMetricData("sharePriceHigh", year, {value:sharePriceHigh, formattedValue:sharePriceHigh.toFixed(2)}, label);
        if(currentData?.[index]?.isThirdYearProjection){
          updatedScenariosData("thirdYearProjectionSharePriceHigh", `$${sharePriceHigh.toFixed(2)}`);
        }
        if(currentData?.[index]?.isFifthYearProjection){
          updatedScenariosData("fifthYearProjectionSharePriceHigh", `$${sharePriceHigh.toFixed(2)}`);
        }
      }
      if(metric === "peHigh" || metric === "peLow") {
        if(currentData?.[index]?.isFifthYearProjection){
          const cgrLow = ( sharePriceLow / marketCurrentPrice ) ** (1/5) - 1 || 0;
          const cgrHigh = ( sharePriceHigh / marketCurrentPrice ) ** (1/5) - 1 || 0;
          console.log("market Price", marketCurrentPrice);
          console.log(`Fifth Year Projection CAGR: ${sharePriceLow} - ${sharePriceHigh}`);
          updatedScenariosData("fifthYearProjectionCGR", `${(cgrLow * 100).toFixed(2)}% - ${(cgrHigh * 100).toFixed(2)}%`);
        }
        if(currentData?.[index]?.isThirdYearProjection){
          const cgrLow = ( sharePriceLow / marketCurrentPrice ) ** (1/3) - 1 || 0;
          const cgrHigh = ( sharePriceHigh / marketCurrentPrice ) ** (1/3) - 1 || 0;
          console.log(`Third Year Projection CAGR: ${sharePriceLow} - ${sharePriceHigh}`);
          updatedScenariosData("thirdYearProjectionCGR", `${(cgrLow * 100).toFixed(2)}% - ${(cgrHigh * 100).toFixed(2)}%`);
        }
      }
    }
    updateMetricData(metric, year, {value:value, formattedValue:value}, label);
  };
  const handleNewScenario = () => {
    const newScenario: Scenario = {
      name: 'Untitled Scenario',
      calcBase: 'prior',
      bull: {
        data: [],
        thirdYearProjectionLabel: '',
        thirdYearProjectionSharePriceLow: '$0',
        thirdYearProjectionSharePriceHigh: '$0',
        thirdYearProjectionCGR: '0%-0%',
        fifthYearProjectionLabel: '',
        fifthYearProjectionSharePriceLow: '$0',
        fifthYearProjectionSharePriceHigh: '$0',
        fifthYearProjectionCGR: '0%-0%',
      },
      base: {
        data: [],
        thirdYearProjectionLabel: '',
        thirdYearProjectionSharePriceLow: '$0',
        thirdYearProjectionSharePriceHigh: '$0',
        thirdYearProjectionCGR: '0%-0%',
        fifthYearProjectionLabel: '',
        fifthYearProjectionSharePriceLow: '$0',
        fifthYearProjectionSharePriceHigh: '$0',
        fifthYearProjectionCGR: '0%-0%',
      },
      bear: {
        data: [],
        thirdYearProjectionLabel: '',
        thirdYearProjectionSharePriceLow: '$0',
        thirdYearProjectionSharePriceHigh: '$0',
        thirdYearProjectionCGR: '0%-0%',
        fifthYearProjectionLabel: '',
        fifthYearProjectionSharePriceLow: '$0',
        fifthYearProjectionSharePriceHigh: '$0',
        fifthYearProjectionCGR: '0%-0%',
      },
    };
    dispatch(setLastScenario(newScenario));
  };
  const saveCurrentScenario = async () => {
    if (researchData.lastScenario?.name == undefined || researchData.lastScenario?.name == '') {
      alertErrorMessage('Please provide a name for the scenario before saving.');
      return;
    }
    if (!symbol) {
      alertErrorMessage('No symbol provided for saving scenario');
      return;
    }
    const findIndex = (researchData.scenario ?? []).findIndex((set: any) => set.name === scenarios?.name);
    let updatedScenarios;
    if (findIndex !== -1) {
      updatedScenarios = [...(researchData.scenario ?? [])];
      updatedScenarios[findIndex] = scenarios;
    } else {
      updatedScenarios = [...(researchData.scenario ?? []), scenarios];
    }
    dispatch(setScenario(updatedScenarios));

    const updatedResearchData = {
      ...researchData,
      scenario: updatedScenarios
    };

    try {
      LoaderHelper.loaderStatus(true);
      if (!symbol) return;
      const result = await AuthService.saveStockResearchData(updatedResearchData, symbol);
      if (result) {
        alertSuccessMessage("Scenario saved successfully!");
      } else {
        alertErrorMessage("Failed to save scenario");
      }
    } catch (error: any) {
      alertErrorMessage(error?.message || "An error occurred while saving the scenario.");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };
  const loadScenarioSet = (scenarioSet: Scenario) => {
    dispatch(setLastScenario(scenarioSet));
    setActiveScenario('bull');
    setIsHistoryOpen(false);
  };
  const updateName = (name: string) => {
    dispatch(updateLastScenarioName(name));
  };
  
  const onChangeCalcBase = (base: 'prior' | 'ttm') => {
    setCalcBase(base);
    dispatch(updateLastScenarioCalcBase(base));
  };
  // console.log('ScenarioEditor - scenarios:', scenarios, 'activeScenario:', activeScenario, 'historyData:', historyData);
  return <div className="relative space-y-4">
    <ScenarioSummary scenarios={scenarios} modelName={scenarios.name} activeScenario={activeScenario} onSelectScenario={setActiveScenario} onUpdateModelName={updateName} onSaveScenario={saveCurrentScenario} onNewScenario={handleNewScenario} onOpenHistory={() => setIsHistoryOpen(true)} isCollapsed={isCardsSectionCollapsed} onToggleCollapse={() => setIsCardsSectionCollapsed(!isCardsSectionCollapsed)} calcBase={calcBase} onChangeCalcBase={onChangeCalcBase} />
    <ScenarioDetail type={activeScenario} history={historyData} scenarioData={scenarios[activeScenario]?.data} onUpdate={(metric, year, value, label, index) => updateScenario(metric, year, value, label, index)} />
    <ScenarioHistory isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} scenarioSets={researchData?.scenario || []} onSelectScenarioSet={loadScenarioSet} />
  </div>;
}